import React from "react";

const MODEL_PROVIDERS = [
    { src: "/assets/technology/openai.svg", alt: "OpenAI", h: 28 },
    { src: "/assets/technology/anthropic.svg", alt: "Anthropic", h: 28 },
    { src: "/assets/technology/gemini.svg", alt: "Google Gemini", h: 28 },
    { src: "/assets/technology/azure.svg", alt: "Microsoft Azure", h: 26 },
    { src: "/assets/technology/elevenlabs.svg", alt: "ElevenLabs", h: 23 },
];

const CHANNELS = [
    { src: "/assets/integrations/whatsapp.svg", alt: "WhatsApp", h: 25 },
    { src: "/assets/integrations/twilio.svg", alt: "Twilio", h: 25 },
    { src: "/assets/integrations/aircall.svg", alt: "Aircall", h: 25 },
    { src: "/assets/integrations/vonage.svg", alt: "Vonage", h: 23 },
    { src: "/assets/integrations/gmail.svg", alt: "Gmail", h: 22 },
    { src: "/assets/integrations/microsoft-outlook.svg", alt: "Outlook", h: 25 },
    { src: "/assets/integrations/microsoft-teams.svg", alt: "Teams", h: 25 },
    { src: "/assets/integrations/slack.svg", alt: "Slack", h: 25 },
    { src: "/assets/integrations/messenger.svg", alt: "Messenger", h: 25 },
    { src: "/assets/integrations/intercom.svg", alt: "Intercom", h: 25 },
    { src: "/assets/integrations/zendesk.svg", alt: "Zendesk", h: 24 },
];

const SYSTEMS = [
    { src: "/assets/integrations/salesforce.svg", alt: "Salesforce", h: 25 },
    { src: "/assets/integrations/hubspot.svg", alt: "HubSpot", h: 25 },
    { src: "/assets/integrations/zoho.svg", alt: "Zoho", h: 25 },
    { src: "/assets/integrations/sap.svg", alt: "SAP", h: 24 },
    { src: "/assets/integrations/oracle.svg", alt: "Oracle", h: 20 },
    { src: "/assets/integrations/stripe.svg", alt: "Stripe", h: 25 },
    { src: "/assets/integrations/google-bigquery.svg", alt: "BigQuery", h: 25 },
    { src: "/assets/integrations/google-sheets.svg", alt: "Google Sheets", h: 25 },
    { src: "/assets/integrations/microsoft-azure.svg", alt: "Azure", h: 24 },
    { src: "/assets/integrations/amazon-s3.svg", alt: "Amazon S3", h: 25 },
    { src: "/assets/integrations/postgresql.svg", alt: "PostgreSQL", h: 25 },
    { src: "/assets/integrations/elevenlabs.svg", alt: "ElevenLabs", h: 23 },
];

const LANES = [
    {
        label: "Modelos",
        title: "Escolha por operação",
        copy: "LLMs e voz variam por SLA, custo, idioma e risco.",
        items: MODEL_PROVIDERS,
        duration: "25s",
        direction: "normal",
    },
    {
        label: "Canais",
        title: "Entrada e resposta",
        copy: "Conversas entram onde o cliente já trabalha.",
        items: CHANNELS,
        duration: "34s",
        direction: "reverse",
    },
    {
        label: "Sistemas",
        title: "Dados e ação",
        copy: "CRM, BI, ERP e storage ficam sob política.",
        items: SYSTEMS,
        duration: "42s",
        direction: "normal",
    },
];

const loopItems = (items) => [...items, ...items, ...items];

const TechStack = () => {
    return (
        <section data-testid="tech-stack-section" className="zild-section zild-stack-section">
            <div className="zild-container">
                <div className="zild-stack-grid">
                    <div className="zild-stack-copy">
                        <h2>
                            Liberdade para escolher e trocar modelos de IA.
                            <span className="zild-serif"> Uma plataforma conectada ao trabalho real.</span>
                        </h2>
                        <p>Use OpenAI, Anthropic, Gemini, Azure ou AWS conforme custo, risco, desempenho e necessidade de cada operação.</p>
                    </div>

                    <div className="zild-stack-board" aria-label="Stack aberto da Zild">
                        <div className="zild-stack-core">
                            <span>Zild</span>
                            <strong>Camada de governança</strong>
                            <small>política · roteamento · evidência</small>
                        </div>

                        <div className="zild-stack-lanes">
                            {LANES.map((lane) => (
                                <StackLane key={lane.label} lane={lane} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .zild-stack-section {
                    background: var(--zild-warm-paper);
                    padding-top: 76px;
                    padding-bottom: 76px;
                    overflow: hidden;
                }

                .zild-stack-grid {
                    display: grid;
                    grid-template-columns: minmax(260px, 0.72fr) minmax(0, 1.28fr);
                    gap: 56px;
                    align-items: center;
                }

                .zild-stack-copy h2 {
                    margin: 0;
                    color: var(--zild-aubergine-ink);
                    font-size: clamp(1.85rem, 2.8vw, 2.45rem);
                    font-weight: 600;
                    line-height: 1.12;
                    letter-spacing: 0;
                }

                .zild-stack-copy h2 span {
                    display: block;
                    color: var(--zild-clay);
                    font-weight: 500;
                }

                .zild-stack-copy > p { margin: 18px 0 0; color: var(--zild-text-muted); font-size: 14px; line-height: 1.65; }

                .zild-stack-board {
                    position: relative;
                    min-width: 0;
                    display: grid;
                    grid-template-columns: minmax(150px, 190px) minmax(0, 1fr);
                    gap: 0;
                    border: 1px solid var(--zild-border-strong);
                    border-radius: var(--zild-radius-lg);
                    background:
                        linear-gradient(90deg, rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.24)),
                        var(--zild-surface);
                    box-shadow: var(--zild-shadow-panel);
                    overflow: hidden;
                }

                .zild-stack-core {
                    position: relative;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    gap: 10px;
                    min-height: 352px;
                    padding: 28px 24px;
                    background: var(--zild-aubergine-nav);
                    color: rgba(255, 255, 255, 0.76);
                }

                .zild-stack-core::after {
                    content: "";
                    position: absolute;
                    top: 34px;
                    right: -1px;
                    bottom: 34px;
                    width: 1px;
                    background: linear-gradient(
                        180deg,
                        transparent,
                        var(--zild-gold),
                        rgba(255, 255, 255, 0.18),
                        var(--zild-teal),
                        transparent
                    );
                }

                .zild-stack-core span {
                    font-family: var(--zild-font-mono);
                    color: var(--zild-gold);
                    font-size: 10px;
                    letter-spacing: 0.12em;
                    text-transform: uppercase;
                }

                .zild-stack-core strong {
                    color: #fff;
                    font-size: 22px;
                    line-height: 1.15;
                    font-weight: 600;
                    letter-spacing: 0;
                }

                .zild-stack-core small {
                    font-family: var(--zild-font-mono);
                    font-size: 11px;
                    line-height: 1.55;
                    color: rgba(255, 255, 255, 0.55);
                }

                .zild-stack-lanes {
                    min-width: 0;
                    display: grid;
                    grid-template-rows: repeat(3, 1fr);
                }

                .zild-stack-lane {
                    min-width: 0;
                    display: grid;
                    grid-template-columns: minmax(148px, 190px) minmax(0, 1fr);
                    align-items: stretch;
                    border-bottom: 1px solid var(--zild-border-soft);
                }

                .zild-stack-lane:last-child {
                    border-bottom: 0;
                }

                .zild-stack-lane__meta {
                    min-width: 0;
                    padding: 22px 20px;
                    border-right: 1px solid var(--zild-border-soft);
                    background: rgba(255, 255, 255, 0.45);
                }

                .zild-stack-lane__meta span {
                    display: block;
                    font-family: var(--zild-font-mono);
                    font-size: 10px;
                    letter-spacing: 0.1em;
                    text-transform: uppercase;
                    color: var(--zild-clay);
                    margin-bottom: 9px;
                }

                .zild-stack-lane__meta strong {
                    display: block;
                    color: var(--zild-aubergine-ink);
                    font-size: 15px;
                    line-height: 1.25;
                    font-weight: 600;
                }

                .zild-stack-lane__meta small {
                    display: block;
                    margin-top: 9px;
                    color: var(--zild-text-muted);
                    font-size: 12px;
                    line-height: 1.45;
                }

                .zild-stack-lane__viewport {
                    min-width: 0;
                    overflow: hidden;
                    display: flex;
                    align-items: center;
                    padding: 16px 0;
                    mask-image: linear-gradient(90deg, transparent, black 8%, black 92%, transparent);
                }

                .zild-stack-lane__track {
                    display: flex;
                    align-items: center;
                    gap: 18px;
                    width: max-content;
                    animation: zildStackScroll var(--stack-speed, 34s) linear infinite;
                    animation-direction: var(--stack-direction, normal);
                }

                .zild-stack-logo {
                    flex: 0 0 auto;
                    display: grid;
                    place-items: center;
                    min-width: 92px;
                    height: 48px;
                    padding: 0 14px;
                    border: 1px solid var(--zild-border);
                    border-radius: var(--zild-radius-md);
                    background: rgba(255, 255, 255, 0.58);
                }

                .zild-stack-logo img {
                    display: block;
                    width: auto;
                    max-width: 100px;
                    object-fit: contain;
                    opacity: 0.86;
                    filter: brightness(0) saturate(100%) invert(12%) sepia(22%) saturate(1019%) hue-rotate(257deg) brightness(91%) contrast(95%);
                    transition: opacity 160ms ease, filter 160ms ease;
                }

                .zild-stack-logo:hover img {
                    opacity: 1;
                    filter: none;
                }

                @keyframes zildStackScroll {
                    from { transform: translateX(0); }
                    to { transform: translateX(-33.333%); }
                }

                @media (prefers-reduced-motion: reduce) {
                    .zild-stack-lane__track {
                        animation-duration: 90s !important;
                    }
                }

                @media (max-width: 960px) {
                    .zild-stack-grid,
                    .zild-stack-board {
                        grid-template-columns: 1fr;
                    }

                    .zild-stack-grid {
                        gap: 34px;
                    }

                    .zild-stack-core {
                        min-height: 0;
                    }

                    .zild-stack-core::after {
                        left: 24px;
                        right: 24px;
                        top: auto;
                        bottom: -1px;
                        width: auto;
                        height: 1px;
                        background: linear-gradient(
                            90deg,
                            transparent,
                            var(--zild-gold),
                            rgba(255, 255, 255, 0.18),
                            var(--zild-teal),
                            transparent
                        );
                    }
                }

                @media (max-width: 680px) {
                    .zild-stack-section {
                        padding-top: 58px;
                        padding-bottom: 58px;
                    }

                    .zild-stack-lane {
                        grid-template-columns: 1fr;
                    }

                    .zild-stack-lane__meta {
                        border-right: 0;
                        border-bottom: 1px solid var(--zild-border-soft);
                        padding: 18px 18px 14px;
                    }

                    .zild-stack-lane__viewport {
                        padding: 14px 0;
                    }
                }
            `}</style>
        </section>
    );
};

const StackLane = ({ lane }) => (
    <div className="zild-stack-lane">
        <div className="zild-stack-lane__meta">
            <span>{lane.label}</span>
            <strong>{lane.title}</strong>
            <small>{lane.copy}</small>
        </div>
        <div className="zild-stack-lane__viewport">
            <div
                className="zild-stack-lane__track"
                style={{
                    "--stack-speed": lane.duration,
                    "--stack-direction": lane.direction,
                }}
            >
                {loopItems(lane.items).map((item, index) => (
                    <div
                        key={`${item.alt}-${item.src}-${index}`}
                        className="zild-stack-logo"
                        aria-hidden={index >= lane.items.length ? "true" : undefined}
                    >
                        <img
                            src={item.src}
                            alt={index < lane.items.length ? item.alt : ""}
                            title={item.alt}
                            loading="eager"
                            decoding="async"
                            width={item.w || item.h}
                            height={item.h}
                            style={{ height: item.h, width: item.w || "auto" }}
                        />
                    </div>
                ))}
            </div>
        </div>
    </div>
);

export default TechStack;
